import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Check, ArrowRight, ArrowLeft, X, ShieldCheck, Mail, Phone, MessageCircle, Maximize2, Minimize2, Wifi, WifiOff, CloudUpload } from "lucide-react";
import { toast } from "sonner";
import { queueSubmission, subscribeConnectivity, flushQueue } from "@/lib/offlineQueue";
import { AnonymousToggle, ConditionalContactInput, isConditionalComplete, ANONYMOUS_NAME } from "@/components/LeadFormExtras";

/* ============================================================
   Public Lead Form entry — chooses between STANDARD and KIOSK
   ============================================================ */
export function LeadFormPublic() {
  const { formSlug } = useParams();
  const [sp] = useSearchParams();
  const kiosk = sp.get("kiosk") === "1";
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const cacheKey = `expoflo:form-ctx:${formSlug}`;
    (async () => {
      try {
        const { data } = await api.get(`/public/lead-form/${formSlug}`);
        setCtx(data);
        try { localStorage.setItem(cacheKey, JSON.stringify(data)); } catch { /* ignore */ }
        api.post(`/public/lead-form/${formSlug}/view`).catch(() => {});
      } catch (e) {
        // Offline / network error — try to serve the last known copy so the
        // kiosk keeps working through a Wi-Fi outage.
        const isNetwork = !e?.response;
        if (isNetwork) {
          try {
            const raw = localStorage.getItem(cacheKey);
            if (raw) { setCtx(JSON.parse(raw)); return; }
          } catch { /* ignore */ }
        }
        setError(e?.response?.data?.detail || "Not available");
      } finally {
        setLoading(false);
      }
    })();
  }, [formSlug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>;
  if (error || !ctx) return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center p-8">
      <div className="font-display text-2xl font-semibold text-gray-900">Not available</div>
      <p className="text-sm text-gray-500 mt-2">This form is not currently active.</p>
    </div>
  );

  return kiosk
    ? <KioskFlow ctx={ctx} formSlug={formSlug} />
    : <StandardForm ctx={ctx} formSlug={formSlug} />;
}

/* ============================================================
   STANDARD form (unchanged — tablet-first landscape)
   ============================================================ */
function StandardForm({ ctx, formSlug }) {
  const [answers, setAnswers] = useState({});
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const startedRef = useRef(false);

  function bumpStart() {
    if (!startedRef.current) {
      startedRef.current = true;
      api.post(`/public/lead-form/${formSlug}/start`).catch(() => {});
    }
  }

  function setAnswer(qid, value) { bumpStart(); setAnswers((p) => ({ ...p, [qid]: value })); }
  function toggleMulti(qid, option) {
    bumpStart();
    setAnswers((p) => {
      const current = Array.isArray(p[qid]) ? p[qid] : [];
      return { ...p, [qid]: current.includes(option) ? current.filter((o) => o !== option) : [...current, option] };
    });
  }

  async function submit(e) {
    e.preventDefault();
    if (!consent) { toast.error("Please accept the consent statement before submitting."); return; }
    const incomplete = (form.questions || []).find((q) => q.type === "contact_conditional" && !isConditionalComplete(q, answers[q.id]));
    if (incomplete) {
      toast.error(`Please complete: ${incomplete.label}`);
      document.querySelector(`[data-testid="answer-${incomplete.id}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/lead-form/${formSlug}/submit`, { answers, consent_given: true });
      setDone({ thank_you_message: data.thank_you_message, redirect_url: data.redirect_url });
      if (data.redirect_url) setTimeout(() => { window.location.href = data.redirect_url; }, 2200);
    } catch (err) { toast.error(err?.response?.data?.detail || "Submit failed"); }
    finally { setSubmitting(false); }
  }

  const { form, organization: org, event } = ctx;

  if (done) return <ThankYou org={org} form={form} done={done} />;

  const questions = [...(form.questions || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const contactMethodQuestion = questions.find((q) => q.type === "single_choice" && q.label.toLowerCase().includes("contact"));

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="lead-form-public">
      <header className="border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {org?.logo_path ? <img src={fileUrl(org.logo_path)} alt={org.name} className="h-8 max-w-[160px] object-contain" data-testid="form-org-logo" onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden'); }} /> : null}
            <div className={org?.logo_path ? "hidden" : ""}><Logo className="h-8" /></div>
            <div className="hidden sm:block h-6 w-px bg-gray-200" />
            <div className="hidden sm:block">
              <div className="text-[10px] uppercase tracking-[0.16em] text-gray-400 font-bold">Registration</div>
              <div className="text-sm font-medium text-gray-800" data-testid="form-org-name">{org?.name}</div>
            </div>
          </div>
          <div className="text-xs text-gray-400">Powered by <span className="text-gray-700 font-medium">ExpoFlo AI</span></div>
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full px-6 lg:px-10 py-8 lg:py-14 grid lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-purple-700 font-bold">{event?.name ? event.name : "Exhibition Registration"}</div>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight text-gray-900 mt-4">{form.name}</h1>
          <p className="text-base text-gray-600 leading-relaxed mt-4 max-w-md">{form.welcome_message || "Tell us what you're interested in and a member of our team will follow up."}</p>
          {event?.venue && <div className="mt-6 text-sm text-gray-500">📍 {event.venue}</div>}
        </aside>

        <section className="lg:col-span-8">
          <form onSubmit={submit} className="space-y-7" data-testid="form-questions">
            {questions.map((q, idx) => (
              <QuestionField
                key={q.id} q={q} idx={idx} value={answers[q.id]}
                onChange={(v) => setAnswer(q.id, v)}
                onToggleMulti={(opt) => toggleMulti(q.id, opt)}
                contactMethod={contactMethodQuestion ? answers[contactMethodQuestion.id] : null}
                allowAnonymous={q.id === form.anonymous_name_question_id}
              />
            ))}

            <div className="border-t border-gray-200 pt-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={consent} onChange={(e) => { bumpStart(); setConsent(e.target.checked); }} className="mt-1 w-4 h-4 accent-purple-600" data-testid="consent-checkbox" />
                <span className="text-sm text-gray-700 leading-relaxed">{form.consent_text}</span>
              </label>
            </div>

            <button type="submit" disabled={submitting || !consent} className="w-full lg:w-auto lg:px-14 brand-gradient text-white rounded-lg py-4 text-base font-medium disabled:opacity-50 transition-colors" data-testid="submit-btn">
              {submitting ? "Submitting…" : "Submit"}
            </button>
          </form>
        </section>
      </main>

      <footer className="border-t border-gray-100 py-4 text-center text-xs text-gray-400">© {new Date().getFullYear()} {org?.name} · Powered by ExpoFlo AI</footer>
    </div>
  );
}

/* ============================================================
   KIOSK FLOW — full screen, welcome + one-question-per-screen
   with progress bar, back/next, 30s auto-reset after submit
   ============================================================ */
/* ============================================================
   KIOSK FLOW — matches attached mockup exactly:
   Welcome → Q1..Q6 (one per screen, 6-segment progress) →
   Consent ("Almost Done!") → Thanks ("Thank You!") → auto-reset
   ============================================================ */
function KioskFlow({ ctx, formSlug }) {
  const { form, organization: org } = ctx;
  const questions = [...(form.questions || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const totalQuestions = questions.length;

  const [stage, setStage] = useState("welcome");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [consent, setConsent] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const showPrivacy = !!form.show_privacy_notice && !!(form.privacy_notice_text || "").trim();
  const [submitting, setSubmitting] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSubmitOffline, setLastSubmitOffline] = useState(false);
  const startedRef = useRef(false);
  const countdownRef = useRef(null);
  const shellRef = useRef(null);
  const mainRef = useRef(null);

  const contactMethodQuestion = questions.find(
    (q) => q.type === "single_choice" && q.label.toLowerCase().includes("contact")
  );

  /* Browser Fullscreen API — Chrome + Safari/webkit */
  useEffect(() => {
    function onChange() {
      setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    }
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  /* On-screen keyboard handling: track visualViewport height and scroll
     the currently-focused input into view so the tablet keyboard never covers it. */
  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) return;

    function updateOffset() {
      // Difference between the layout viewport and the visible area = keyboard height
      const diff = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardOffset(diff);
    }

    function scrollFocusedIntoView() {
      const el = document.activeElement;
      if (!el) return;
      const tag = el.tagName;
      if (tag !== "INPUT" && tag !== "TEXTAREA") return;
      // Small delay lets the keyboard animation settle
      setTimeout(() => {
        try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch { /* ignore */ }
      }, 120);
    }

    function onFocusIn(e) {
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) {
        scrollFocusedIntoView();
      }
    }

    vv.addEventListener("resize", updateOffset);
    vv.addEventListener("scroll", updateOffset);
    document.addEventListener("focusin", onFocusIn);
    updateOffset();
    return () => {
      vv.removeEventListener("resize", updateOffset);
      vv.removeEventListener("scroll", updateOffset);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, []);

  /* When the keyboard opens, re-scroll the currently focused input into view */
  useEffect(() => {
    if (keyboardOffset <= 0) return;
    const el = document.activeElement;
    if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA")) {
      setTimeout(() => {
        try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch { /* ignore */ }
      }, 60);
    }
  }, [keyboardOffset]);

  /* Connectivity: reflect online/offline state, expose pending count, auto-flush queued submissions */
  useEffect(() => {
    const unsub = subscribeConnectivity({
      onChange: ({ online: on, pending }) => {
        setOnline(on);
        setPendingCount(pending);
      },
    });
    return unsub;
  }, []);

  async function toggleFullscreen({ silent = false } = {}) {
    try {
      const inFs = document.fullscreenElement || document.webkitFullscreenElement;
      if (!inFs) {
        const el = shellRef.current || document.documentElement;
        if (el.requestFullscreen) await el.requestFullscreen();
        else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
      } else {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
      }
    } catch (err) {
      if (!silent) toast.error("Fullscreen is not available on this device.");
    }
  }

  function bumpStart() {
    if (!startedRef.current) {
      startedRef.current = true;
      api.post(`/public/lead-form/${formSlug}/start`).catch(() => {});
    }
  }

  function reset() {
    setStage("welcome"); setStep(0); setAnswers({}); setConsent(false); setPrivacyAccepted(false);
    setSubmitting(false); setCountdown(null); setLastSubmitOffline(false);
    startedRef.current = false;
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  }

  useEffect(() => () => { if (countdownRef.current) clearInterval(countdownRef.current); }, []);

  function startCountdown() {
    let n = 15;
    setCountdown(n);
    countdownRef.current = setInterval(() => {
      n -= 1; setCountdown(n);
      if (n <= 0) { clearInterval(countdownRef.current); countdownRef.current = null; reset(); }
    }, 1000);
  }

  function setAnswer(qid, v) { bumpStart(); setAnswers((p) => ({ ...p, [qid]: v })); }
  function toggleMulti(qid, opt) {
    bumpStart();
    setAnswers((p) => {
      const cur = Array.isArray(p[qid]) ? p[qid] : [];
      return { ...p, [qid]: cur.includes(opt) ? cur.filter((o) => o !== opt) : [...cur, opt] };
    });
  }

  function canProceed() {
    if (stage !== "question") return true;
    const q = questions[step];
    if (q.type === "contact_conditional") return isConditionalComplete(q, answers[q.id]);
    if (!q.required) return true;
    const v = answers[q.id];
    if (v === undefined || v === null) return false;
    if (typeof v === "string" && !v.trim()) return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  }

  function next() {
    bumpStart();
    if (!canProceed()) return;
    if (stage === "welcome") {
      if (showPrivacy && !privacyAccepted) return;
      // Try to enter browser fullscreen on the user-initiated Start click (silent — no toast if blocked)
      if (!(document.fullscreenElement || document.webkitFullscreenElement)) {
        toggleFullscreen({ silent: true }).catch(() => {});
      }
      setStage("question"); setStep(0); return;
    }
    if (stage === "question") {
      if (step < questions.length - 1) setStep(step + 1);
      else setStage("consent");
    }
  }

  function back() {
    if (stage === "consent") { setStage("question"); setStep(questions.length - 1); return; }
    if (stage === "question") {
      if (step > 0) setStep(step - 1);
      else setStage("welcome");
    }
  }

  async function submit() {
    if (!consent) return;
    setSubmitting(true);
    // Stable client id so retries dedupe server-side
    const clientSubmissionId = `csid_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    const payload = { answers, consent_given: true, client_submission_id: clientSubmissionId };
    try {
      await api.post(`/public/lead-form/${formSlug}/submit`, payload);
      setLastSubmitOffline(false);
      setStage("thanks");
      startCountdown();
      // Best-effort: drain any older offline submissions now that we know we're online
      flushQueue().then((r) => setPendingCount(r.remaining)).catch(() => {});
    } catch (err) {
      const isNetwork = !err?.response;
      if (isNetwork) {
        // Offline / unreachable — queue locally and pretend we're done.
        // Auto-sync will retry when connectivity returns.
        queueSubmission(formSlug, payload);
        setPendingCount((c) => c + 1);
        setLastSubmitOffline(true);
        setStage("thanks");
        startCountdown();
      } else {
        toast.error(err?.response?.data?.detail || "Submit failed");
      }
    } finally { setSubmitting(false); }
  }

  const showProgress = stage === "question";
  const stepNumber = stage === "question" ? step + 1 : 0;

  return (
    <div ref={shellRef} className="fixed inset-0 z-50 bg-white flex flex-col overflow-hidden" data-testid="kiosk-shell">
      <header className="shrink-0 border-b border-gray-100">
        <div className="max-w-4xl mx-auto w-full px-8 md:px-12 pt-8 pb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {org?.logo_path ? (
              <img src={fileUrl(org.logo_path)} alt={org.name} className="h-9 max-w-[180px] object-contain"
                onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextElementSibling?.classList.remove("hidden"); }}
                onLoad={(e) => { if (e.currentTarget.naturalWidth <= 2) { e.currentTarget.style.display = "none"; e.currentTarget.nextElementSibling?.classList.remove("hidden"); } }} />
            ) : null}
            <div className={org?.logo_path ? "hidden" : ""}><Logo className="h-9" /></div>
          </div>
          {showProgress && (
            <div className="flex items-center gap-3">
              <ConnectivityBadge online={online} pending={pendingCount} />
              <div className="text-sm text-gray-500 font-medium" data-testid="kiosk-step-label">
                Step {stepNumber} of {totalQuestions}
              </div>
              <button
                onClick={toggleFullscreen}
                className="text-gray-400 hover:text-gray-900 p-1.5 rounded-full transition-colors"
                data-testid="kiosk-fullscreen-btn-inline"
                title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              </button>
            </div>
          )}
          {!showProgress && (
            <div className="flex items-center gap-3">
              <ConnectivityBadge online={online} pending={pendingCount} />
              <button
                onClick={toggleFullscreen}
                className="text-gray-500 hover:text-gray-900 border border-gray-200 hover:border-gray-300 rounded-full p-2 transition-colors"
                data-testid="kiosk-fullscreen-btn"
                title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
            </div>
          )}
        </div>

        {showProgress && (
          <div className="max-w-4xl mx-auto w-full px-8 md:px-12 pb-6">
            <div className="flex gap-2" data-testid="kiosk-progress-bar">
              {Array.from({ length: totalQuestions }).map((_, i) => (
                <div key={i} className="flex-1 h-2 rounded-full overflow-hidden bg-gray-200">
                  <div className={`h-full brand-gradient transition-all duration-500 ${i < stepNumber ? "w-full" : "w-0"}`}
                    data-testid={`progress-seg-${i}`} />
                </div>
              ))}
            </div>
          </div>
        )}
      </header>

      <main
        ref={mainRef}
        className="flex-1 min-h-0 overflow-y-auto flex"
        style={keyboardOffset > 0 ? { paddingBottom: `${keyboardOffset}px` } : undefined}
        data-testid="kiosk-main"
      >
        <div className="max-w-4xl mx-auto w-full px-8 md:px-12 py-10 md:py-14 flex flex-col justify-center">

          {stage === "welcome" && (
            <div className="fade-up text-center" data-testid="kiosk-welcome">
              {org?.logo_path && (
                <img src={fileUrl(org.logo_path)} alt={org.name} className="h-14 max-w-[220px] object-contain mx-auto mb-8"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  onLoad={(e) => { if (e.currentTarget.naturalWidth <= 2) e.currentTarget.style.display = "none"; }} />
              )}
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-gray-900">Welcome</h1>
              <p className="text-base md:text-lg text-gray-600 mt-4">{form.welcome_message || "We'd love to hear from you."}</p>
              <p className="text-sm text-gray-500 mt-3 max-w-lg mx-auto leading-relaxed">
                Please complete the short form below and an authorised representative will get in touch.
              </p>

              {showPrivacy && (
                <div
                  className="mt-8 mx-auto max-w-xl rounded-2xl bg-purple-50/60 border border-purple-100 p-5 md:p-6 text-left"
                  data-testid="kiosk-privacy-notice"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck size={18} className="text-purple-700 shrink-0" />
                    <div className="text-xs uppercase tracking-[0.18em] text-purple-800 font-bold">Privacy Notice</div>
                  </div>
                  <div className="text-sm text-gray-700 leading-relaxed max-h-40 md:max-h-48 overflow-y-auto pr-1 whitespace-pre-line" data-testid="kiosk-privacy-text">
                    {form.privacy_notice_text}
                  </div>
                  <label className="mt-4 flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={privacyAccepted}
                      onChange={(e) => setPrivacyAccepted(e.target.checked)}
                      className="mt-0.5 w-5 h-5 accent-purple-600 shrink-0"
                      data-testid="kiosk-privacy-checkbox"
                    />
                    <span className="text-sm text-gray-800 leading-relaxed">
                      I have read and understand the Privacy Notice.
                    </span>
                  </label>
                </div>
              )}

              <button onClick={next}
                disabled={showPrivacy && !privacyAccepted}
                className="mt-10 brand-gradient text-white rounded-full px-12 md:px-16 py-4 md:py-5 text-base md:text-lg font-medium inline-flex items-center gap-3 hover:brightness-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="kiosk-start-btn">
                Start Now <ArrowRight size={18} />
              </button>
              {showPrivacy && !privacyAccepted && (
                <p className="text-xs text-gray-400 mt-3" data-testid="kiosk-start-hint">
                  Please read and accept the Privacy Notice to continue.
                </p>
              )}
            </div>
          )}

          {stage === "question" && (
            <div className="fade-up" data-testid={`kiosk-question-${step}`} key={step}>
              <h2 className="font-display text-2xl md:text-3xl lg:text-[2.25rem] font-semibold tracking-tight text-gray-900 leading-snug">
                {stepNumber}. {questions[step].label}
              </h2>
              <p className="text-sm md:text-base text-gray-500 mt-3">{questionSubtitle(questions[step])}</p>
              <div className="mt-8">
                <KioskQuestionInput q={questions[step]} value={answers[questions[step].id]}
                  onChange={(v) => setAnswer(questions[step].id, v)}
                  onToggleMulti={(opt) => toggleMulti(questions[step].id, opt)}
                  contactMethod={contactMethodQuestion ? answers[contactMethodQuestion.id] : null}
                  allowAnonymous={questions[step].id === form.anonymous_name_question_id} />
              </div>
            </div>
          )}

          {stage === "consent" && (
            <div className="fade-up text-center max-w-2xl mx-auto" data-testid="kiosk-consent">
              <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-gray-900">Almost Done!</h2>
              <p className="text-base text-gray-500 mt-4">Please review and consent before submitting your information.</p>
              <label className="mt-8 flex items-start gap-3 text-left border border-purple-200 bg-purple-50/50 rounded-xl px-5 py-5 cursor-pointer hover:border-purple-400 transition-colors">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 w-5 h-5 accent-purple-600 shrink-0" data-testid="kiosk-consent-checkbox" />
                <div>
                  <div className="text-sm md:text-base text-gray-900 leading-relaxed">
                    {form.consent_text || "I agree that my information may be used to contact me about the products or services I have expressed interest in."}
                  </div>
                  <div className="text-xs text-gray-500 mt-2">You can withdraw your consent at any time.</div>
                </div>
              </label>
            </div>
          )}

          {stage === "thanks" && (
            <div className="fade-up text-center max-w-xl mx-auto" data-testid="kiosk-thanks">
              <div className="w-24 h-24 rounded-full border-2 border-gray-900 mx-auto flex items-center justify-center text-gray-900 mb-8">
                <Check size={40} strokeWidth={2.5} />
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-gray-900">Thank You!</h2>
              <p className="text-base text-gray-600 mt-4">Your information has been submitted successfully.</p>
              <p className="text-sm text-gray-500 mt-1">An authorised representative will contact you soon.</p>
              {lastSubmitOffline && (
                <div
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2 text-xs md:text-sm"
                  data-testid="kiosk-thanks-offline-note"
                >
                  <CloudUpload size={14} />
                  Saved on this device — it will sync automatically once Wi-Fi is back.
                </div>
              )}
              <button onClick={reset}
                className="mt-10 brand-gradient text-white rounded-full px-14 py-4 text-base font-medium inline-flex items-center gap-2 hover:brightness-110 transition-all"
                data-testid="kiosk-done-btn">
                Done
              </button>
              {countdown !== null && (
                <div className="mt-6 text-xs text-gray-400" data-testid="kiosk-countdown">Ready for the next visitor in {countdown}s…</div>
              )}
            </div>
          )}
        </div>
      </main>

      {(stage === "question" || stage === "consent") && (
        <footer className="shrink-0 border-t border-gray-100 bg-white">
          <div className="max-w-4xl mx-auto w-full px-8 md:px-12 py-5 flex items-center justify-between gap-4">
            <button onClick={back}
              className="text-gray-800 border border-gray-200 rounded-full px-6 py-3 text-sm md:text-base font-medium inline-flex items-center gap-2 hover:border-gray-400 transition-colors"
              data-testid="kiosk-back-btn">
              <ArrowLeft size={16} /> Back
            </button>
            {stage === "question" ? (
              <button onClick={next} disabled={!canProceed()}
                className="brand-gradient text-white rounded-full px-8 md:px-10 py-3 text-sm md:text-base font-medium inline-flex items-center gap-2 disabled:opacity-40 hover:brightness-110 transition-all"
                data-testid="kiosk-next-btn">
                Next <ArrowRight size={16} />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting || !consent}
                className="brand-gradient text-white rounded-full px-8 md:px-10 py-3 text-sm md:text-base font-medium inline-flex items-center gap-2 disabled:opacity-40 hover:brightness-110 transition-all"
                data-testid="kiosk-submit-btn">
                {submitting ? "Submitting…" : "Submit"} <Check size={16} />
              </button>
            )}
          </div>
        </footer>
      )}

      <button onClick={reset} className="absolute top-3 right-3 text-gray-300 hover:text-gray-600 p-1" data-testid="kiosk-abort" title="Reset session">
        <X size={14} />
      </button>
    </div>
  );
}

function ConnectivityBadge({ online, pending }) {
  const hasPending = pending > 0;
  if (online && !hasPending) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2.5 py-1"
        title="Connected"
        data-testid="kiosk-connectivity-online"
      >
        <Wifi size={12} /> Online
      </span>
    );
  }
  if (!online) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1"
        title="Working offline — submissions are saved on this device"
        data-testid="kiosk-connectivity-offline"
      >
        <WifiOff size={12} /> Offline{hasPending ? ` · ${pending} pending` : ""}
      </span>
    );
  }
  // online but flushing
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs text-purple-800 bg-purple-50 border border-purple-100 rounded-full px-2.5 py-1"
      title="Syncing saved submissions"
      data-testid="kiosk-connectivity-syncing"
    >
      <CloudUpload size={12} /> Syncing {pending}
    </span>
  );
}

function questionSubtitle(q) {  if (q.type === "short_text") return `Please enter your ${q.label.toLowerCase().replace(/\?$/, "")}.`;
  if (q.type === "long_text") return "Optional.";
  if (q.type === "single_choice") return q.label.toLowerCase().includes("contact") ? "Select one option." : "Select one option.";
  if (q.type === "multi_choice") return "Select all that apply.";
  if (q.type === "contact_dynamic") return "Please enter your details so we can reach you.";
  if (q.type === "contact_conditional") return "Select Yes to share your contact details.";
  return "";
}

function KioskQuestionInput({ q, value, onChange, onToggleMulti, contactMethod, allowAnonymous }) {
  if (q.type === "short_text") {
    const anon = allowAnonymous && value === ANONYMOUS_NAME;
    return (
      <div>
        <input type="text" required={q.required} value={anon ? "" : value || ""} disabled={anon} onChange={(e) => onChange(e.target.value)}
          placeholder={anon ? "You're staying anonymous" : `Enter your ${q.label.toLowerCase().replace(/\?$/, "").replace(/what is your /, "")}`}
          className="w-full border border-gray-200 rounded-xl px-5 py-4 text-base md:text-lg outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 disabled:bg-gray-50"
          data-testid={`answer-${q.id}`} />
        {allowAnonymous && <AnonymousToggle value={value} onChange={onChange} kiosk />}
      </div>
    );
  }
  if (q.type === "contact_conditional") return <ConditionalContactInput q={q} value={value} onChange={onChange} kiosk />;
  if (q.type === "long_text") {
    return (
      <textarea rows={5} value={value || ""} onChange={(e) => onChange(e.target.value)}
        placeholder="Type your message here…"
        className="w-full border border-gray-200 rounded-xl px-5 py-4 text-base md:text-lg outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 resize-none"
        data-testid={`answer-${q.id}`} />
    );
  }
  if (q.type === "single_choice") {
    const isContact = q.label.toLowerCase().includes("contact");
    if (isContact) {
      const iconMap = { Email: Mail, Phone: Phone, WhatsApp: MessageCircle };
      return (
        <div className="grid grid-cols-3 gap-3 md:gap-4" data-testid={`answer-${q.id}`}>
          {q.options.map((opt) => {
            const Icon = iconMap[opt] || Mail;
            const selected = value === opt;
            return (
              <button key={opt} type="button" onClick={() => onChange(opt)}
                className={`border-2 rounded-xl px-4 py-6 md:py-8 flex flex-col items-center justify-center gap-3 transition-colors relative ${selected ? "border-purple-600 bg-purple-50" : "border-gray-200 hover:border-gray-300"}`}
                data-testid={`single-${q.id}-${opt.toLowerCase().replace(/\W+/g, "-")}`}>
                <span className={`absolute top-3 right-3 w-4 h-4 rounded-full border ${selected ? "border-purple-600 bg-purple-600" : "border-gray-300"} flex items-center justify-center`}>
                  {selected && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                </span>
                <Icon size={26} strokeWidth={1.5} className={selected ? "text-purple-700" : "text-gray-700"} />
                <span className={`text-sm md:text-base font-medium ${selected ? "text-purple-900" : "text-gray-800"}`}>{opt}</span>
              </button>
            );
          })}
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`answer-${q.id}`}>
        {q.options.map((opt) => {
          const selected = value === opt;
          return (
            <button key={opt} type="button" onClick={() => onChange(opt)}
              className={`border rounded-xl px-5 py-4 text-left text-base transition-colors ${selected ? "border-purple-600 bg-purple-50 text-purple-900 font-medium" : "border-gray-200 hover:border-gray-300"}`}
              data-testid={`single-${q.id}-${opt.toLowerCase().replace(/\W+/g, "-")}`}>
              {opt}
            </button>
          );
        })}
      </div>
    );
  }
  if (q.type === "multi_choice") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`answer-${q.id}`}>
        {q.options.map((opt) => {
          const selected = Array.isArray(value) && value.includes(opt);
          return (
            <button key={opt} type="button" onClick={() => onToggleMulti(opt)}
              className={`flex items-center gap-3 border rounded-xl px-5 py-4 text-left text-base transition-colors ${selected ? "border-purple-600 bg-purple-50 text-purple-900 font-medium" : "border-gray-200 hover:border-gray-300"}`}
              data-testid={`multi-${q.id}-${opt.toLowerCase().replace(/\W+/g, "-")}`}>
              <span className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${selected ? "bg-purple-600 border-purple-600 text-white" : "border-gray-300"}`}>
                {selected && <Check size={12} strokeWidth={3} />}
              </span>
              {opt}
            </button>
          );
        })}
      </div>
    );
  }
  if (q.type === "contact_dynamic") return <KioskContactDynamic q={q} value={value || ""} contactMethod={contactMethod} onChange={onChange} />;
  return null;
}

function KioskContactDynamic({ q, value, contactMethod, onChange }) {
  const method = (contactMethod || "").toLowerCase();
  let type = "text"; let placeholder = "Enter your details"; let pattern; let helper = "";
  if (method === "email") { type = "email"; placeholder = "Enter your email address"; helper = "We will contact you via email."; }
  else if (method === "phone") { type = "tel"; placeholder = "Enter your phone number"; pattern = "^\\+?[0-9\\s()-]{6,}$"; helper = "We will contact you by phone."; }
  else if (method === "whatsapp") { type = "tel"; placeholder = "Enter your WhatsApp number"; pattern = "^\\+?[0-9\\s()-]{6,}$"; helper = "We will contact you via WhatsApp."; }
  if (!contactMethod) {
    return (
      <div className="text-sm text-gray-400 border border-dashed border-gray-200 rounded-xl px-5 py-4" data-testid={`answer-${q.id}`}>
        Please go back and select your preferred contact method first.
      </div>
    );
  }
  return (
    <div data-testid={`answer-${q.id}`}>
      <input type={type} required={q.required} pattern={pattern} value={value}
        onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full border border-gray-200 rounded-xl px-5 py-4 text-base md:text-lg outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
        data-testid={`contact-value-${method}`} />
      {helper && <div className="text-xs text-gray-500 mt-2">{helper}</div>}
    </div>
  );
}

function ThankYou({ done }) {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <div className="w-16 h-16 rounded-full brand-gradient mx-auto flex items-center justify-center text-white mb-6"><Check size={32} /></div>
        <div className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-gray-900" data-testid="form-thank-you">
          {done.thank_you_message || "Thank you!"}
        </div>
        <p className="text-sm text-gray-500 mt-3">Your details have been submitted successfully.</p>
        {done.redirect_url && <p className="text-xs text-gray-400 mt-2">Redirecting you shortly…</p>}
      </div>
    </div>
  );
}

/* ============================================================
   Shared QuestionField — used by both Standard and Kiosk flows
   ============================================================ */
function QuestionField({ q, idx, value, onChange, onToggleMulti, contactMethod, allowAnonymous, kiosk = false }) {
  const inputClass = kiosk
    ? "w-full border border-gray-200 rounded-lg px-5 py-4 text-lg outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
    : "mt-3 w-full border border-gray-200 rounded-lg px-4 py-3.5 text-base outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20";

  const container = (children) => kiosk ? children : (
    <div data-testid={`q-${idx + 1}`}>
      <label className="text-xs uppercase tracking-[0.14em] font-bold text-gray-500">
        {String(idx + 1).padStart(2, "0")} — {q.label} {q.required && <span className="text-pink-600">*</span>}
      </label>
      {children}
    </div>
  );

  if (q.type === "short_text") {
    const anon = allowAnonymous && value === ANONYMOUS_NAME;
    return container(
      <>
        <input type="text" required={q.required && !anon} disabled={anon} value={anon ? "" : value || ""} placeholder={anon ? "You're staying anonymous" : ""}
          onChange={(e) => onChange(e.target.value)} className={`${inputClass} disabled:bg-gray-50`} data-testid={`answer-${q.id}`} />
        {allowAnonymous && <AnonymousToggle value={value} onChange={onChange} />}
      </>
    );
  }

  if (q.type === "contact_conditional") return container(
    <ConditionalContactInput q={q} value={value} onChange={onChange} />
  );

  if (q.type === "long_text") return container(
    <textarea required={q.required} rows={kiosk ? 5 : 4} value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${inputClass} resize-none`} data-testid={`answer-${q.id}`} />
  );

  if (q.type === "single_choice") return container(
    <div className={`${kiosk ? "" : "mt-3"} grid grid-cols-1 sm:grid-cols-2 ${kiosk ? "md:grid-cols-3" : "sm:grid-cols-3"} gap-3`} data-testid={`answer-${q.id}`}>
      {q.options.map((opt) => {
        const selected = value === opt;
        return (
          <button key={opt} type="button" onClick={() => onChange(opt)}
            className={`border rounded-lg ${kiosk ? "px-5 py-4 text-base" : "px-4 py-3 text-sm"} text-left transition-colors ${selected ? "border-purple-600 bg-purple-50 text-purple-900 font-medium" : "border-gray-200 hover:border-gray-300"}`}
            data-testid={`single-${q.id}-${opt.toLowerCase().replace(/\W+/g, "-")}`}>
            {opt}
          </button>
        );
      })}
    </div>
  );

  if (q.type === "multi_choice") return container(
    <div className={`${kiosk ? "" : "mt-3"} grid grid-cols-1 sm:grid-cols-2 gap-3`} data-testid={`answer-${q.id}`}>
      {q.options.map((opt) => {
        const selected = Array.isArray(value) && value.includes(opt);
        return (
          <button key={opt} type="button" onClick={() => onToggleMulti(opt)}
            className={`flex items-center gap-2 border rounded-lg ${kiosk ? "px-5 py-4 text-base" : "px-4 py-3 text-sm"} text-left transition-colors ${selected ? "border-purple-600 bg-purple-50 text-purple-900 font-medium" : "border-gray-200 hover:border-gray-300"}`}
            data-testid={`multi-${q.id}-${opt.toLowerCase().replace(/\W+/g, "-")}`}>
            <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${selected ? "bg-purple-600 border-purple-600 text-white" : "border-gray-300"}`}>
              {selected && <Check size={10} strokeWidth={3} />}
            </span>
            {opt}
          </button>
        );
      })}
    </div>
  );

  if (q.type === "contact_dynamic") return container(
    <ContactDynamicInput q={q} value={value || ""} contactMethod={contactMethod} onChange={onChange} kiosk={kiosk} />
  );

  return null;
}

function ContactDynamicInput({ q, value, contactMethod, onChange, kiosk }) {
  const method = (contactMethod || "").toLowerCase();
  let label = "Contact details";
  let type = "text"; let placeholder = ""; let pattern;
  if (method === "email") { label = "Email address"; type = "email"; placeholder = "you@company.com"; }
  else if (method === "phone") { label = "Phone number"; type = "tel"; placeholder = "+267 71 234 567"; pattern = "^\\+?[0-9\\s()-]{6,}$"; }
  else if (method === "whatsapp") { label = "WhatsApp number"; type = "tel"; placeholder = "+267 71 234 567"; pattern = "^\\+?[0-9\\s()-]{6,}$"; }

  const wrap = kiosk ? "" : "mt-3";
  const inputClass = kiosk
    ? "w-full border border-gray-200 rounded-lg px-5 py-4 text-lg outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
    : "w-full border border-gray-200 rounded-lg px-4 py-3.5 text-base outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20";

  if (!contactMethod) return (
    <div className={`${wrap} text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg px-4 py-3`} data-testid={`answer-${q.id}`}>
      Please select your preferred contact method above to enter your details.
    </div>
  );

  return (
    <div className={wrap} data-testid={`answer-${q.id}`}>
      <div className="text-xs text-gray-500 mb-2">{label}</div>
      <input type={type} required={q.required} pattern={pattern} value={value}
        onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={inputClass}
        data-testid={`contact-value-${method}`} />
    </div>
  );
}
